import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import GridMediaCard from "../../GridMediaCard/GridMediaCard";
import {
  getSelectedHashtagFeedCollection,
  getSelectedFeedItems,
} from "selectors/hashtagFeedSelectors";
import { addCollectionAssetsFromFeed } from "actions/mediaLibrary";
import { clearFeedSelection, toggleFeedMedia } from "actions/sourceMedia";
import { getCollections } from "selectors/mediaLibrarySelectors";
import { getRoutinesLoading } from "selectors/commonSelectors";
import { ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE } from "constants/ActionTypes";
import { CollectionSelectorModern } from "@/features/media-library/components/collection-selector/collection-selector-modern";
import {
  assetFromFeedMedia,
  FeedMedia,
} from "components/MediaLibrary/SourceMedia/FeedGrid/FeedGrid";
import { MediaCollection } from "api/media-library";
import type { ReduxRootState } from "@/types/redux-store";

export function FeedSelectedAssets() {
  const dispatch = useDispatch();
  const selectedFeedItems = useSelector(getSelectedFeedItems) as FeedMedia[];
  const selectedHashtagFeedCollection = useSelector(
    getSelectedHashtagFeedCollection
  );
  const collections = useSelector(getCollections) as MediaCollection[];
  const isSaving = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE])
  );

  const [selectedCollectionId, setSelectedCollectionId] = useState<string>(
    selectedHashtagFeedCollection?._id || ""
  );

  const handleSave = () => {
    const collection = collections.find((c) => c._id === selectedCollectionId);
    if (!collection || selectedFeedItems.length === 0) return;

    dispatch(
      addCollectionAssetsFromFeed({
        collectionId: collection._id,
        feedItems: selectedFeedItems,
        location: "library",
      })
    );
  };

  const handleDeselectAll = () => {
    dispatch(clearFeedSelection());
  };

  const handleRemoveItem = (media: FeedMedia) => {
    dispatch(toggleFeedMedia(media));
  };

  if (selectedFeedItems.length === 0) {
    return null;
  }

  return (
    <div className="relative p-4 min-h-[170px] h-full">
      <div className="h-full flex flex-col items-start text-left">
        {/* Header row with count and deselect */}
        <div className="flex items-center gap-2">
          <h3 className="font-gotham font-semibold text-sm text-neutral-700 leading-none">
            {selectedFeedItems.length} item
            {selectedFeedItems.length === 1 ? "" : "s"} selected
          </h3>
          <span className="text-neutral-400 text-sm leading-none">·</span>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 font-gotham text-sm leading-none text-blue-500 hover:text-blue-600"
            onClick={handleDeselectAll}
            disabled={isSaving}
            aria-label="Deselect all items"
          >
            Deselect All
          </Button>
        </div>

        {/* Content */}
        <div className="flex-shrink min-h-0 grid grid-cols-[1fr_max-content] mt-2 gap-4 self-stretch">
          <div>
            <div className="flex flex-wrap gap-2">
              {selectedFeedItems.map((media) => (
                <GridMediaCard
                  className="h-24 w-24"
                  key={media.id}
                  asset={assetFromFeedMedia(media)}
                  canFavorite={false}
                  showCheckbox={false}
                  showActionButtons={false}
                  onCardClick={() => !isSaving && handleRemoveItem(media)}
                />
              ))}
            </div>
          </div>

          {/* Collection selector and Save button */}
          <div className="flex flex-col gap-2">
            <CollectionSelectorModern
              collections={collections}
              selectedCollection={
                collections.find((c) => c._id === selectedCollectionId) || null
              }
              onChange={(value) => setSelectedCollectionId(value as string)}
              disabled={isSaving}
            />
            <Button
              variant="blue-primary"
              size="md"
              className="w-full"
              disabled={
                !selectedCollectionId ||
                selectedFeedItems.length === 0 ||
                isSaving
              }
              onClick={handleSave}
            >
              {isSaving ? (
                <>
                  <Spinner className="h-4 w-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa-regular fa-save mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
